import { useMemo, useState, type ReactElement } from 'react';

import { useTranslation } from '@govard/i18n-core/react';

import { useMemorySearch } from '../hooks/useMemorySearch.js';
import { useRecentMemories } from '../hooks/useRecentMemories.js';

type MemoryTab = 'l1' | 'l2' | 'l3';

export function MemoryBrowserPage(): ReactElement {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<MemoryTab>('l1');
  const [query, setQuery] = useState('');
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<Array<{ id: string; key: string; valueText: string; score: number }>>([]);

  const l1Query = useRecentMemories('l1', 30);
  const l2Query = useRecentMemories('l2', 30);
  const l3Query = useRecentMemories('l3', 30);
  const searchMutation = useMemorySearch();

  const activeItems = useMemo(() => {
    if (activeTab === 'l1') {
      return l1Query.data?.items ?? [];
    }
    if (activeTab === 'l2') {
      return l2Query.data?.items ?? [];
    }
    return l3Query.data?.items ?? [];
  }, [activeTab, l1Query.data?.items, l2Query.data?.items, l3Query.data?.items]);

  const searchItems = searchResults;

  async function handleSearch(): Promise<void> {
    const normalized = query.trim();
    if (normalized.length === 0) {
      return;
    }
    setLastQuery(normalized);
    const result = await searchMutation.mutateAsync({ query: normalized, limit: 10 });
    setSearchResults(result.items);
  }

  return (
    <main
      className="animate-fade-in flex flex-col p-6 mx-auto w-full"
      style={{
        backgroundColor: 'var(--color-bg, #fcfaf8)',
        minHeight: '100vh',
        maxWidth: 1200,
        gap: 20,
      }}
    >
      <div className="mb-2">
        <h1 className="font-bold tracking-tight" style={{ fontSize: 24, color: 'var(--color-text, #2d2418)' }}>
          {t('crm.memory.title')}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--color-text-muted, #a89b8a)', marginTop: 4 }}>
          {t('crm.memory.subtitle')}
        </p>
      </div>

      <div className="card flex flex-col" style={{ padding: 24 }}>
        <div className="mb-5">
          <h2 className="font-semibold" style={{ fontSize: 16, color: 'var(--color-text, #2d2418)' }}>
            {t('crm.memory.browser_title')}
          </h2>
          <p style={{ fontSize: 12, color: 'var(--color-text-muted, #a89b8a)', marginTop: 4 }}>
            {t('crm.memory.browser_description')}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label={t('crm.memory.tabs_aria')}>
          {(['l1', 'l2', 'l3'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              role="tab"
              aria-selected={activeTab === tab}
              type="button"
              className="btn transition-colors"
              style={{
                padding: '6px 16px',
                fontSize: 13,
                backgroundColor: activeTab === tab ? 'var(--color-primary, #c47322)' : 'transparent',
                color: activeTab === tab ? '#ffffff' : 'var(--color-text-secondary, #6b5e4f)',
                border: activeTab === tab ? '1px solid var(--color-primary, #c47322)' : '1px solid var(--color-border, #ddd7cc)',
              }}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>

        {activeTab === 'l3' ? (
          <section className="flex flex-col gap-4 mb-6 rounded-lg" style={{ padding: 16, backgroundColor: 'var(--color-surface-hover, #f0ece5)', border: '1px solid var(--color-border, #ddd7cc)' }}>
            <div className="flex flex-col gap-2 sm:flex-row">
              <label className="sr-only" htmlFor="memory-search-input">
                {t('crm.memory.search_label')}
              </label>
              <input
                id="memory-search-input"
                data-testid="memory-search-input"
                className="input w-full"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('crm.memory.search_placeholder')}
                aria-label={t('crm.memory.search_label')}
              />
              <button 
                type="button" 
                data-testid="memory-search-btn"
                className="btn btn-primary whitespace-nowrap" 
                onClick={() => void handleSearch()} 
                disabled={searchMutation.isPending}
                style={{ height: 36, fontSize: 13 }}
              >
                {t('crm.memory.search_submit')}
              </button>
            </div>

            {searchMutation.isPending ? (
              <div className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--color-text-muted, #a89b8a)' }}>
                <span className="inline-block animate-spin">⟳</span>
                <span>{t('crm.memory.search_loading')}</span>
              </div>
            ) : null}

            {searchItems.length > 0 ? (
              <div className="flex flex-col gap-2" data-testid="memory-search-results">
                {searchItems.map((item) => (
                  <article 
                    key={item.id} 
                    className="rounded-lg transition-colors"
                    style={{ 
                      padding: '12px 16px', 
                      backgroundColor: 'var(--color-surface, #ffffff)',
                      border: '1px solid var(--color-border, #ddd7cc)'
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium" style={{ fontSize: 14, color: 'var(--color-text, #2d2418)' }}>
                        {item.key}
                      </p>
                      <span 
                        className="rounded-full flex-shrink-0"
                        style={{ 
                          fontSize: 10, 
                          padding: '2px 8px', 
                          backgroundColor: 'var(--color-surface-hover, #f0ece5)',
                          border: '1px solid var(--color-border, #ddd7cc)',
                          color: 'var(--color-text-secondary, #6b5e4f)'
                        }}
                      >
                        {t('crm.memory.score', { score: item.score.toFixed(2) })}
                      </span>
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--color-text-muted, #a89b8a)', marginTop: 4 }}>
                      {item.valueText}
                    </p>
                    <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--color-border, #ddd7cc)' }}>
                      <button type="button" className="btn btn-light" style={{ fontSize: 12, padding: '4px 8px' }}>
                        {t('crm.memory.actions.view')}
                      </button>
                      <button type="button" className="btn text-red-600 hover:bg-red-50" style={{ fontSize: 12, padding: '4px 8px' }}>
                        {t('crm.memory.actions.forget')}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}

            {lastQuery !== null && searchItems.length === 0 && !searchMutation.isPending ? (
              <p style={{ fontSize: 13, color: 'var(--color-text-muted, #a89b8a)' }}>
                {t('crm.memory.search_empty', { query: lastQuery })}
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="flex flex-col gap-3" data-testid={`memory-tab-${activeTab}`}>
          {activeItems.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--color-text-muted, #a89b8a)', padding: '32px 0', textAlign: 'center' }}>
              {t('crm.memory.empty')}
            </p>
          ) : null}
          {activeItems.map((item) => (
            <article 
              key={item.id} 
              className="rounded-lg transition-colors"
              style={{ 
                border: '1px solid var(--color-border, #ddd7cc)', 
                padding: '12px 16px',
                backgroundColor: 'var(--color-surface, #ffffff)',
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium" style={{ fontSize: 14, color: 'var(--color-text, #2d2418)' }}>
                  {item.key}
                </p>
                <span 
                  className="rounded-full flex-shrink-0"
                  style={{ 
                    fontSize: 10, 
                    padding: '2px 8px', 
                    backgroundColor: 'var(--color-surface-hover, #f0ece5)',
                    color: 'var(--color-text-secondary, #6b5e4f)'
                  }}
                >
                  {t('crm.memory.importance', { value: item.importance })}
                </span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--color-text-muted, #a89b8a)', marginTop: 4 }}>
                {item.valueText}
              </p>
            </article>
          ))}
        </section>
      </div>
    </main>
  );
}
